import React from 'react';
import { AcornProduct, ProductType } from '../generated/types-and-hooks';
import { generateImageCdnPath, ImageSize } from './images';
import _ from 'lodash';
import log from 'loglevel';

const PRICE_CURRENCY = 'USD';

const determineAvailability = (
  InStock: number,
  BackorderLimit: number,
  Display: Boolean,
) => {
  if (!Display) {
    return 'https://schema.org/Discontinued';
  }
  if (InStock > 0) {
    return 'https://schema.org/InStock';
  }
  if (BackorderLimit > 0) {
    return 'https://schema.org/PreOrder';
  }
  return 'https://schema.org/OutOfStock';
};

export const productTitle = (product: AcornProduct): string => {
  if (product.BotanicalName && product.CommonName) {
    return `${product.CommonName} (${product.BotanicalName}) Plant`;
  }
  if (product.Name) {
    return `${product.Name} Plant`;
  }
  log.error(
    { product },
    'cannot generate product title.  Product Requires CommonName/BotanicalName or Name.  Falling back to returning Id where it should be text!',
  );
  return `${product.Id} Plant`;
};

export type ProductLDInput = {
  siteUrl: string;
  product: AcornProduct;
};
export const ProductLD = ({
  siteUrl,
  product,
}: ProductLDInput): JSX.Element => {
  const image = _.map(product.ImageHashes, (hash) =>
    generateImageCdnPath(hash!, ImageSize.Medium),
  );
  const url = `${siteUrl}/organic-plants/${product.Slug}`;
  const name = productTitle(product);

  let offers = [
    {
      '@type': 'Offer',
      name: `${name} - 3 in. potted plant`,
      url,
      priceCurrency: PRICE_CURRENCY,
      price: String(product.Price),
      itemCondition: 'http://schema.org/NewCondition',
      availability: determineAvailability(
        product.InStock,
        product.BackorderLimit,
        product.DisplayMVG,
      ),
    },
    {
      '@type': 'Offer',
      name: `${name} - 1 ct. Plugtray (128 potted plant plugs per tray)`,
      url,
      priceCurrency: PRICE_CURRENCY,
      price: String(product.PricePlugtrays),
      itemCondition: 'http://schema.org/NewCondition',
      availability: determineAvailability(
        product.InStockPlugtrays,
        0,
        product.DisplayPlugtrays,
      ),
    },
  ];
  if (product.Type === ProductType.Collection) {
    offers = [
      {
        '@type': 'Offer',
        name: `${name} - Collection (${product.CollectionProductCount} - 3 in. potted plants)`,
        url,
        priceCurrency: PRICE_CURRENCY,
        price: String(product.Price),
        itemCondition: 'http://schema.org/NewCondition',
        availability: determineAvailability(
          product.InStock,
          product.BackorderLimit,
          product.DisplayMVG,
        ),
      },
    ];
  }

  return (
    <script id="product-ld" type="application/ld+json" key="product-ld">
      {JSON.stringify({
        '@context': 'http://schema.org/',
        '@type': 'Product',
        '@id': product.Id,
        name,
        url,
        sku: product.Id,
        mpn: product.Id,
        // "color": product.Colo,
        height: product.Height,
        image,
        description: product.Description,
        brand: {
          '@type': 'Thing',
          name: 'Mountain Valley Growers',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: 0,
          reviewCount: 0,
        },
        offers,
      })}
    </script>
  );
};

const productListElement = (
  siteUrl: string,
  product: AcornProduct,
  index: number,
) => ({
  '@type': 'ListItem',
  position: index + 1,
  name: productTitle(product),
  url: `${siteUrl}/organic-plants/${product.Slug}`,
});

export type CollectionPageProductsLDInput = {
  siteUrl: string;
  products: AcornProduct[];
  title: string;
};

export const CollectionPageProductsLD = ({
  siteUrl,
  products,
  title,
}: CollectionPageProductsLDInput) => {
  return (
    <script id="collection-ld" type="application/ld+json">
      {JSON.stringify({
        '@context': 'http://schema.org',
        '@type': 'CollectionPage',
        name: title,
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: _.map(
            products,
            _.partial(productListElement, siteUrl),
          ),
        },
      })}
    </script>
  );
};
